import React, { useEffect, useMemo, useState } from "react";
import { Calendar, Clock, Mail, Reply } from "lucide-react";
import Datatable from "../components/tables/Datatable";
import { useDispatch, useSelector } from "react-redux";
import { fetchInquiries, respondToInquiry } from "../redux/slices/inquirySlice";
import Loader from "../components/Loader";
import Swal from "sweetalert2";
import Modal from "../components/models/Modal";
import toast from "react-hot-toast";

const Manageinquiry = () => {
  const dispatch = useDispatch();
  const {
    list,
    loading,
    currentPage,
    totalPages,
  } = useSelector((state) => state.inquiries || { list: [], loading: false, currentPage: 1, totalPages: 1 });

  const [page, setPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [initialLoad, setInitialLoad] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [isRespondModalOpen, setIsRespondModalOpen] = useState(false);
  const [teamResponse, setTeamResponse] = useState("");
  const [statusVal, setStatusVal] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const token = useSelector((state) => state.auth.token);

  useEffect(() => {
    if (!token) return;

    dispatch(
      fetchInquiries({ page, perPage: entriesPerPage, search: searchQuery })
    ).finally(() => {
      setInitialLoad(false);
    });
  }, [dispatch, page, entriesPerPage, searchQuery, token]);

  const handleOpenRespondModal = (inquiry) => {
    setSelectedInquiry(inquiry);
    setTeamResponse(inquiry.team_response || "");
    setStatusVal(inquiry.status ?? 1);
    setIsRespondModalOpen(true);
  };

  const handleRespondSubmit = async (e) => {
    e.preventDefault();
    if (!teamResponse.trim()) {
      toast.error("Please enter a response message.");
      return;
    }

    setIsSubmitting(true);
    try {
      const queryId = selectedInquiry.query_id || selectedInquiry.id;
      await dispatch(
        respondToInquiry({
          query_id: queryId,
          status: Number(statusVal),
          team_response: teamResponse.trim(),
        })
      ).unwrap();

      Swal.fire({
        title: "Success!",
        text: "Response submitted successfully.",
        icon: "success",
        confirmButtonText: "OK",
      });

      setIsRespondModalOpen(false);
      // Refetch queries
      dispatch(
        fetchInquiries({ page, perPage: entriesPerPage, search: searchQuery })
      );
    } catch (err) {
      Swal.fire("Error!", err || "Failed to submit response.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add serial number mapping
  const mappedData = useMemo(() => {
    if (!list) return [];
    return list.map((item, index) => ({
      ...item,
      srNo: (page - 1) * entriesPerPage + index + 1,
    }));
  }, [list, page, entriesPerPage]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const handleEntriesChange = (newLimit) => {
    setEntriesPerPage(newLimit);
    setPage(1);
  };

  const handleSearchChange = (query) => {
    setSearchQuery(query);
    setPage(1);
  };

  const columns = [
    {
      header: "Sr. No.",
      accessor: "srNo",
      render: (value) => <span>{value}</span>,
    },
    {
      header: "Inquirer Details",
      accessor: "name",
      render: (_, row) => (
        <div className="flex gap-2">
          <div className="flex justify-center items-center">
            <span className="bg-[#fff0ec] text-[#ff7448] font-bold text-sm px-3 py-2 rounded-md">
              {(() => {
                if (!row.name) return "";
                const words = row.name.trim().split(/\s+/);
                if (words.length === 1) {
                  return words[0].slice(0, 2).toUpperCase();
                }
                return (words[0][0] + (words[1] ? words[1][0] : "")).toUpperCase();
              })()}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="font-bold text-xs">{row.name}</span>
            <span className="font-bold text-xs">{row.email}</span>
            <span className="font-bold text-xs">
              {row.country_code ? `${row.country_code} ` : ""}{row.phone_number}
            </span>
          </div>
        </div>
      ),
    },
    {
      header: "Country",
      accessor: "country",
      render: (country) => (
        <p className="font-bold text-xs text-gray-800">{country || "N/A"}</p>
      ),
    },
    {
      header: "Query Message",
      accessor: "query_message",
      render: (msg) => (
        <div className="max-w-xs break-words text-xs text-gray-800 font-bold">
          <p className="line-clamp-2" title={msg}>
            {msg}
          </p>
        </div>
      ),
    },
    {
      header: "Team Response",
      accessor: "team_response",
      render: (team_response) => (
        <div className="max-w-xs break-words text-xs text-gray-800 font-bold">
          <p className="line-clamp-2" title={team_response || "No response yet"}>
            {team_response || <span className="text-gray-400 font-normal italic">No response yet</span>}
          </p>
        </div>
      ),
    },
    {
      header: "Status",
      accessor: "status",
      render: (status) => {
        const isResponded = status === 1 || status === "1";
        return (
          <span
            className={`px-2 py-1 rounded-md text-xs font-bold ${
              isResponded
                ? "bg-green-100 text-green-800"
                : "bg-yellow-100 text-yellow-800"
            }`}
          >
            {isResponded ? "Responded" : "Pending"}
          </span>
        );
      },
    },
    {
      header: "Created Date And Time",
      accessor: "created_at",
      render: (created_at) => {
        if (!created_at) return "-";
        const parts = created_at.split(" ");
        const dateVal = parts[0] || "";
        const timeVal = parts[1] ? `${parts[1]} ${parts[2] || ""}`.trim() : "";
        return (
          <div className="flex flex-col">
            <span className="flex items-center text-xs text-black font-bold">
              <Calendar className="w-4 h-4 mr-2 text-black" /> {dateVal}
            </span>
            <span className="flex items-center text-xs text-black font-bold mt-1">
              <Clock className="w-4 h-4 mr-2 text-black" /> {timeVal}
            </span>
          </div>
        );
      },
    },
    {
      header: "Actions",
      accessor: "id",
      render: (_, row) => {
        const isResponded = row.status === 1 || row.status === "1";
        return (
          <div className="flex gap-2">
            <button
              onClick={() => handleOpenRespondModal(row)}
              disabled={isResponded}
              className={`flex items-center gap-1.5 text-xs font-bold py-1.5 px-3 rounded-md transition-all duration-300 ${
                isResponded
                  ? "text-gray-400 bg-gray-100 cursor-not-allowed border border-gray-200"
                  : "text-white bg-[#FF6943] hover:bg-[#e05631] cursor-pointer"
              }`}
            >
              <Reply size={14} />
              Respond
            </button>
          </div>
        );
      },
    },
  ];

  if (initialLoad && loading) {
    return <Loader />;
  }

  return (
    <>
      <div className="w-full bg-white rounded-md p-5 border border-gray-200 shadow-sm">
        <Datatable
          title="Manage Inquiries"
          isloading={loading}
          columns={columns}
          data={mappedData}
          searchKeys={["name", "email", "query_message", "country"]}
          paginationInfo={{
            currentPage: page,
            totalPages: totalPages,
            entriesPerPage: entriesPerPage,
            onPageChange: handlePageChange,
            onEntriesChange: handleEntriesChange,
            onSearchChange: handleSearchChange,
          }}
        />
      </div>

      {/* Respond Modal */}
      <Modal
        isOpen={isRespondModalOpen}
        onClose={() => setIsRespondModalOpen(false)}
        title="Respond to Inquiry"
        footer={
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setIsRespondModalOpen(false)}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm transition"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              onClick={handleRespondSubmit}
              disabled={isSubmitting}
              className="bg-[#FF6943] hover:bg-[#e05631] text-white px-4 py-2 rounded-lg text-sm flex items-center justify-center gap-2 transition"
            >
              {isSubmitting ? (
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              ) : (
                "Send Response"
              )}
            </button>
          </div>
        }
      >
        {selectedInquiry && (
          <div className="space-y-4">
            {/* Inquiry Details Card */}
            <div className="bg-gray-50 border border-gray-100 p-4 rounded-lg text-xs space-y-2">
              <div className="flex justify-between">
                <span className="font-semibold text-gray-500">Inquirer:</span>
                <span className="text-gray-800 font-bold">{selectedInquiry.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-semibold text-gray-500">Email:</span>
                <span className="text-gray-800">{selectedInquiry.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-semibold text-gray-500">Phone:</span>
                <span className="text-gray-800">
                  {selectedInquiry.country_code ? `${selectedInquiry.country_code} ` : ""}{selectedInquiry.phone_number}
                </span>
              </div>
              <div className="border-t border-gray-200 my-2"></div>
              <div>
                <span className="font-semibold text-gray-500 block mb-1">Query Message:</span>
                <p className="text-gray-700 bg-white p-2 border rounded border-gray-150 break-words leading-relaxed">
                  {selectedInquiry.query_message}
                </p>
              </div>
            </div>

            {/* Response Form */}
            <form className="space-y-4" onSubmit={handleRespondSubmit}>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-700">Status</label>
                <select
                  value={statusVal}
                  onChange={(e) => setStatusVal(Number(e.target.value))}
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700 bg-gray-50 focus:outline-none focus:ring-1 focus:ring-[#FF6943] focus:border-[#FF6943]"
                >
                  <option value={0}>Pending</option>
                  <option value={1}>Resolved</option>
                </select>
              </div>

              <div className="flex flex-col gap-1">
                <label className="text-xs font-semibold text-gray-700">Team Response</label>
                <textarea
                  rows={4}
                  value={teamResponse}
                  onChange={(e) => setTeamResponse(e.target.value)}
                  placeholder="Enter response details/resolution actions taken..."
                  className="border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-800 bg-white focus:outline-none focus:ring-1 focus:ring-[#FF6943] focus:border-[#FF6943]"
                ></textarea>
              </div>
            </form>
          </div>
        )}
      </Modal>
    </>
  );
};

export default Manageinquiry;